"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseStoreUnit = void 0;
const deepCopy_1 = require("../deep-copy/deepCopy");
const validate_1 = require("../validate");
/**
 * **BaseStoreUnit**
 *
 * This is a class to transform an object to a `view representation` (to be returned to the consumer) or a `store representation` (to be stored in the database).
 * The majority of the time these will be the same, however in some cases we may want strip out values from either of these, this is controlled via the
 * `excludedStoreFields` and `excludedViewFields` options.
 *
 * **Overview of infromation flow:**
 * - `RawObject` - Object provided to the service from somewhere (this could be the webApp or another service). This may contain extra fields we don't care about
 * - `ViewObject` - The representation to be returned to the client
 * - `StoreObject` - The object to be persisted to the DB
 *
 * **Flow 1 - Persist an object:**
 *
 * `RawObject` received -> BaseStoreUnit strips out any `excludedStoreFields` -> `StoreObject` -> Persist to DB
 *
 * **Flow 2 - Retrieve an object from the DB:**
 *
 *  `StoreObject` fetched from DB -> BaseStoreUnit strips out any `excludedViewFields` -> `ViewObject` -> Returned to the Client
 *
 *
 * **Typescript Generics:**
 * - ___StoreObjectT___ - type of the object stored in the DB i.e. ModelT minus ignoredFields
 * - ___ViewObjectT___ - type of the view object i.e. the store object minus excludedViewFields
 */
class BaseStoreUnit {
    constructor(model, { excludedStoreFields = [], excludedViewFields = [] } = {}) {
        this.excludedStoreFields = excludedStoreFields;
        this.excludedViewFields = excludedViewFields;
        const parsedModel = Object.keys(model).reduce((acc, keyName) => {
            if (!excludedStoreFields.includes(keyName)) {
                acc[keyName] = (0, deepCopy_1.deepCopy)(model[keyName]);
            }
            return acc;
        }, {});
        this.model = parsedModel;
    }
    static parseStoreObject(model, ...args) {
        let parentName;
        try {
            // @ts-ignore
            parentName = new this({}).constructor.name;
        }
        catch (err) { }
        throw new Error(`parseStoreObject needs to be implemented on ${parentName ? parentName : 'your StoreUnit'} before you can use it.`);
    }
    static parseViewObject(model, ...args) {
        let parentName;
        try {
            // @ts-ignore
            parentName = new this({}).constructor.name;
        }
        catch (err) { }
        throw new Error(`parseViewObject needs to be implemented on ${parentName ? parentName : 'your StoreUnit'} before you can use it.`);
    }
    toStoreObject() {
        const storeObj = {};
        Object.keys(this.model).forEach(key => {
            const value = this.model[key];
            if ((0, validate_1.isNullOrUndef)(value)) {
                return;
            }
            if (this.excludedStoreFields.includes(key)) {
                return;
            }
            storeObj[key] = (0, deepCopy_1.deepCopy)(value);
        });
        return storeObj;
    }
    toView() {
        const view = {};
        Object.keys(this.model).forEach(key => {
            if (this.excludedViewFields.includes(key)) {
                return;
            }
            view[key] = (0, deepCopy_1.deepCopy)(this.model[key]);
        });
        return view;
    }
}
exports.BaseStoreUnit = BaseStoreUnit;
